import React, { FC } from 'react';

import { Chip } from '@material-ui/core';
import { DateTime } from 'luxon';
import { useHistory } from 'react-router-dom';

import Button from 'Components-redesign/Button';
import { Heading } from 'Components-redesign/Typography/Heading';
import { Paragraph } from 'Components-redesign/Typography/Paragraph';
import { useI18n } from 'Hooks';
import { GetLatestReleases_superAdminViewer_latestReleases_items } from 'Scenes/Releases/api/__queries__';
import { ReleaseNoteWrapper, ReleaseWrapper } from 'Scenes/Releases/styles';
import { Box } from 'styles/box';

interface Props {
  releaseNotes: GetLatestReleases_superAdminViewer_latestReleases_items[];
}

/** Component that lists all releases with type RELEASE_NOTES */
const ReleaseNotesList: FC<Props> = ({ releaseNotes }) => {
  const { t } = useI18n();
  const history = useHistory();

  const formatDate = (date: string) => {
    return DateTime.fromISO(date).toFormat('yyyy-MM-dd HH:mm');
  };

  return (
    <>
      {releaseNotes.map((release) => (
        <ReleaseWrapper
          key={release.id}
          padding="medium"
          display="flex"
          flexDirection="column"
          gap="xs">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Paragraph level={5} color="var(--colorBlueGray800)">
              {t('Releases.UpdatedAt')}: {formatDate(release.updatedAt)}
            </Paragraph>

            <Paragraph level={5} color="var(--colorBlueGray800)">
              {t('Releases.CreatedBy')}: {release.createdBy}
            </Paragraph>
          </Box>

          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Heading level={2}>{t(release.titleLang.en)}</Heading>
            <Button
              text={t('General.Edit')}
              width="120px"
              height="40px"
              styleType="Dark"
              handleClick={() => history.push(`/releases?id=${release.id}&type=release-notes`)}
            />
          </Box>

          <Box display="flex" flexDirection="row" gap="xs" marginBottom="xs">
            {release.productTags.map((tag) => (
              <Chip key={tag} size="small" label={tag} />
            ))}
          </Box>

          <Box display="flex" flexDirection="column" gap="xs">
            {release.notes.map((note, index) => (
              <ReleaseNoteWrapper
                key={index}
                padding="small"
                display="flex"
                flexDirection="column"
                gap="xs">
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Paragraph level={4}>{t(note.titleLang.en)}</Paragraph>
                  {note.showToCustomers && (
                    <Chip size="small" label="Show to customers" color="primary" />
                  )}
                </Box>
              </ReleaseNoteWrapper>
            ))}
          </Box>

          {release.imageUrls.length > 0 && (
            <Box
              display="flex"
              flexDirection="row"
              gap="xs"
              padding="xs"
              style={{ backgroundColor: 'var(--colorBlueGray100)', borderRadius: '8px' }}>
              {release.imageUrls.map((url) => (
                <img
                  key={url}
                  src={url}
                  alt="Release"
                  style={{ height: '100px', borderRadius: '8px' }}
                />
              ))}
            </Box>
          )}
        </ReleaseWrapper>
      ))}
    </>
  );
};

export default ReleaseNotesList;
